import { useEffect, useRef, useState } from 'react'
import { Camera, Download, Trash2 } from 'lucide-react'
import { addJobPhotos, captionJobPhoto, deleteJobPhoto, jobPhotoUrl, listJobPhotos } from '../../api/jobs'
import Button from '../../components/Button'
import HelpTip from '../../components/HelpTip'
import Modal from '../../components/Modal'
import { Card } from '../../components/Summary'
import { useToast } from '../../components/Toast.jsx'
import { fmtDateTime } from '../../utils/format'
import { shrinkImage } from '../../utils/shrinkImage'

const HELP = 'Before and after, measurements, damage, the finished work. On a phone, Add photos offers the camera or your photos; they’re made smaller before they go up. Only people who can open this job see them.'

/** A job's photos: add them (camera or library), see them, caption or remove them. */
export default function JobPhotos({ job }) {
  const { notify } = useToast()
  const input = useRef(null)
  const [photos, setPhotos] = useState(null)
  const [uploading, setUploading] = useState(0)
  const [error, setError] = useState('')
  const [shown, setShown] = useState(null) // the photo open in the viewer
  const [caption, setCaption] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    listJobPhotos(job.job_id).then(setPhotos).catch(() => setPhotos([]))
  }, [job.job_id])

  const add = async (list) => {
    const files = Array.from(list || [])
    if (input.current) input.current.value = ''
    if (!files.length) return
    setError(''); setUploading(files.length)
    try {
      const small = await Promise.all(files.map((f) => shrinkImage(f)))
      setPhotos(await addJobPhotos(job.job_id, small))
      notify(files.length === 1 ? 'Photo added' : `${files.length} photos added`)
    } catch (e) { setError(e.message || 'Couldn’t add the photos.') }
    setUploading(0)
  }
  const open = (p) => { setShown(p); setCaption(p.caption || '') }
  const saveCaption = async () => {
    if ((shown.caption || '') === caption.trim()) return setShown(null)
    setBusy(true)
    try { setPhotos(await captionJobPhoto(shown.photo_id, caption.trim())); setShown(null) } catch (e) { setError(e.message || 'Couldn’t save the caption.') }
    setBusy(false)
  }
  const remove = async () => {
    if (!window.confirm('Remove this photo from the job?')) return
    setBusy(true)
    try { setPhotos(await deleteJobPhoto(shown.photo_id)); setShown(null); notify('Photo removed') } catch (e) { setError(e.message || 'Couldn’t remove the photo.') }
    setBusy(false)
  }

  return (
    <div id="photos">
      <Card
        title={<>Photos <HelpTip text={HELP} /></>}
        action={(
          <button type="button" className="sum-card-link" disabled={uploading > 0} onClick={() => input.current?.click()}>
            {uploading ? `Adding ${uploading}…` : 'Add photos'}
          </button>
        )}
      >
        <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => add(e.target.files)} />
        {error && <p className="sum-warn">{error}</p>}
        {photos === null ? <p className="sum-muted">Loading…</p> : photos.length === 0 ? (
          <button type="button" className="jp-empty" onClick={() => input.current?.click()}>
            <Camera size={18} aria-hidden="true" />
            <span>No photos yet. Add before-and-after shots, measurements or damage.</span>
          </button>
        ) : (
          <div className="jp-grid">
            {photos.map((p) => (
              <button key={p.photo_id} type="button" className="jp-thumb" onClick={() => open(p)} title={p.caption || p.file_name}>
                <img src={jobPhotoUrl(p.photo_id)} alt={p.caption || p.file_name} loading="lazy" />
                {p.caption && <span>{p.caption}</span>}
              </button>
            ))}
          </div>
        )}
      </Card>

      <Modal
        open={!!shown}
        onClose={() => setShown(null)}
        title={shown?.caption || 'Photo'}
        subtitle={shown ? [fmtDateTime(shown.uploaded_at), shown.uploaded_by].filter(Boolean).join(' · ') : ''}
        size="lg"
        footer={shown && (
          <>
            <Button variant="ghost" onClick={remove} disabled={busy}><Trash2 size={14} />Remove</Button>
            <Button as="a" variant="ghost" href={jobPhotoUrl(shown.photo_id, { download: true })}><Download size={14} />Download</Button>
            <Button onClick={saveCaption} loading={busy}>Done</Button>
          </>
        )}
      >
        {shown && (
          <div className="jp-view">
            <img src={jobPhotoUrl(shown.photo_id)} alt={shown.caption || shown.file_name} />
            <label className="ci-form-field">
              <span>Caption (optional)</span>
              <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Before: kitchen wall" maxLength={255} />
            </label>
          </div>
        )}
      </Modal>
    </div>
  )
}
